import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { MdInsertComment } from "react-icons/md";
import { IoIosSend } from "react-icons/io";
import { X } from "react-bootstrap-icons";
import toast from "react-hot-toast";
import clsx from "clsx";
import { /* isNull, */ isEmpty, isNil } from "lodash";
import { isMobile, isIOS } from "react-device-detect";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

import ResponsiveWrapper from "../../../../../components/common/ResponsiveWrapper";
import AutoResizeTextarea from "../../../../../components/common/AutoResizeTextarea";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { useUser } from "../../../../../context/UserContext";
import {
    createComment,
    getPropertyComments,
    // getUserGuess,
    deleteComment,
    getUserComments,
} from "../../../../../services/UserService";
import { getUserReactionsForProperty } from "../../../../../services/ReactionService";
import {
    getTagComments,
    createTagComment,
    deleteTagComment,
    getPropertyTagsComments,
} from "../../../../../services/TagsService";
import { LIVE_VARIANT_VERSIONS } from "../../../../../constants/live-variant-versions";
import Comment from "./Comment";
import CommentSkeleton from "./CommentSkeleton";
// import UserGuessSection from "./UserGuessSection";
import useCustomLoginWithRedirect from "../../../../../hooks/useCustomLoginWithRedirect";
import { useParams } from "react-router-dom";
import { FIVE_MINUTES, THIRTY_MINUTES } from "../../../../../constants/time";
import TagList from "../../../../../components/common/TagList";
import TagVoteBar from "./TagVoteBar";
import useMixpanelEvent from "../../../../../hooks/useMixpanelEvent";
import { determineCommentType } from "../../../../../helpers/commentHelpers";

const ITEMS_PER_PAGE = 10;
const STALE_TIME = FIVE_MINUTES; // 5 minutes
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes
const DEFAULT_DRAWER_HEIGHT = "90vh";
const DELETED_COMMENT_TEXT = "Comment deleted";

const unicodeReactionPromptMap = {
    "U+1F60D": "What do you love about it?",
    "U+1F92F": "What surprised you about it?",
    "U+1F4B0": "What do you think of the price?",
    "U+1F3AF": "What’s so great about this location?",
    "U+1FAE4": "What would you change?",
};

/**
 * CommentsModal component displays a modal with comments for a property
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.handleClose - Function to close the modal
 * @param {string} props.propertyId - ID of the property to show comments for
 * @param {string} props.tag - The tag associated with the comments
 * @param {string[]} props.tags - An array of tags associated with the comments
 */
const CommentsModal = ({ show, handleClose, propertyId, tag, tags = [] }) => {
    const hideTag = LIVE_VARIANT_VERSIONS.social.hideTag;
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const [comment, setComment] = useState("");
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyingToCommentType, setReplyingToCommentType] = useState(null); // 'tag' or 'normal'
    const [replyingToTagId, setReplyingToTagId] = useState(null); // store tag ID if it's a tag comment
    const [socialState, setSocialState] = useLocalStorage("social-state");
    const property = socialState.property;
    const queryClient = useQueryClient();
    const userId = userState?.id;
    const commentsContainerRef = useRef(null);
    const inputRef = useRef(null);
    const [drawerHeight, setDrawerHeight] = useState(DEFAULT_DRAWER_HEIGHT);
    const { listing_address } = useParams();
    const navigate = useNavigate();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [showBeNice, setShowBeNice] = useState(false);

    /*  const { data: previousGuessData } = useQuery({
        queryKey: ["user-guess", propertyId, userId],
        queryFn: () => getUserGuess(userId, propertyId, null, null, null, "social"),
        enabled: !isNull(userId) && !isNull(propertyId),
    }); */
    const [selectedTag, setSelectedTag] = useState(tag);
    const pageName = isEmpty(listing_address) ? "Property Feed - Trending" : "Property Details";

    // Find tag object for current selectedTag (if tags prop is array of objects)
    const tagObj = tags.find((t) => t.id === selectedTag?.id);
    const tagCreatorId = tagObj?.creator?.id;
    const taggableId = tagObj?.taggableId;
    const hideVoteButtons = !userId || tagCreatorId === userId;

    const { data: userReactions } = useQuery({
        queryKey: ["user-reactions", userId, propertyId],
        queryFn: () => getUserReactionsForProperty(userId, propertyId, "ASC"),
        enabled: !!userId && !!propertyId,
    });

    const { data: usercommentsData } = useQuery({
        queryKey: ["userComments", userId],
        queryFn: ({ pageParam = 1 }) => getUserComments(userId, pageParam, ITEMS_PER_PAGE),
        enabled: !!userId,
        staleTime: STALE_TIME, // Consider data fresh for 5 minutes
        cacheTime: CACHE_TIME, // Keep data in cache for 30 minutes
    });

    const {
        data: commentsData,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteQuery(
        selectedTag ? ["comments", propertyId, selectedTag.id] : ["comments", propertyId],
        ({ pageParam = 1 }) => {
            if (selectedTag) {
                // If a specific tag is selected, get comments for that tag
                return getTagComments(propertyId, selectedTag.id, pageParam, ITEMS_PER_PAGE);
            } else {
                // If no tag is selected, get all property comments including tag comments
                return Promise.allSettled([
                    getPropertyComments(propertyId, pageParam, ITEMS_PER_PAGE),
                    getPropertyTagsComments(propertyId, pageParam, ITEMS_PER_PAGE),
                ]).then((results) => {
                    // results[0] = getPropertyComments, results[1] = getPropertyTagsComments
                    const regularComments =
                        results[0].status === "fulfilled" ? results[0].value : { data: [], nextPage: null };
                    const tagComments =
                        results[1].status === "fulfilled" ? results[1].value : { data: [], nextPage: null };

                    // Combine and sort the comments
                    const combinedData = {
                        data: [...regularComments.data, ...tagComments.data],
                        nextPage: regularComments.nextPage || tagComments.nextPage,
                    };
                    return combinedData;
                });
            }
        },
        {
            getNextPageParam: (lastPage) => {
                if (!lastPage.data || lastPage.data.length < ITEMS_PER_PAGE) {
                    return undefined;
                }
                return lastPage.nextPage;
            },
            staleTime: STALE_TIME,
            cacheTime: CACHE_TIME,
            enabled: !!propertyId,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
        }
    );

    // Process comments to include nested replies
    const processComments = (comments) => {
        // Filter out comments with parent_id (replies) since they're already nested
        const rootComments = comments.filter((comment) => !comment.parent_id);
        return rootComments;
    };

    const allComments = commentsData?.pages.flatMap((page) => page.data) ?? [];
    const commentsToDisplay = processComments(allComments);

    // Update selectedTag when tag prop changes
    useEffect(() => {
        setSelectedTag(tag);
    }, [tag]);

    // Track "View Comments" event when modal is shown
    useEffect(() => {
        if (show && !tag) {
            sendMixpanelEvent("View Comments", {
                pageName,
                nickname: userState?.nickname,
                email: userState?.email_address,
            });
        }
    }, [show, tag, pageName, userState?.email_address, userState?.nickname, sendMixpanelEvent]);

    // Add function to determine comment level
    const getCommentLevel = (parentId) => {
        if (!parentId) return "1st level";

        // Find the parent comment
        const foundComment = allComments.find((comment) => comment.id === parentId);
        if (!foundComment) return "2nd level";

        // If parent comment has a parent_id, it's a 3rd level comment
        return foundComment.parent_id ? "3rd level" : "2nd level";
    };

    const { mutate: submitComment, isSubmitting } = useMutation({
        mutationFn: ({ content, parentId }) => {
            if (selectedTag) {
                // If a specific tag is selected, use tag comment endpoints
                if (parentId) {
                    return createTagComment(userId, propertyId, selectedTag.id, content, parentId);
                } else {
                    return createTagComment(userId, propertyId, selectedTag.id, content);
                }
            } else {
                // If no tag is selected, use the stored comment type information
                if (parentId && replyingToCommentType === "tag" && replyingToTagId) {
                    return createTagComment(userId, propertyId, replyingToTagId, content, parentId);
                } else {
                    // Replying to a normal comment or creating a new comment
                    return createComment(userId, propertyId, content, parentId);
                }
            }
        },
        onSuccess: () => {
            // Track comment event on successful submission
            sendMixpanelEvent("Enter Comment", {
                isAuthenticated: true,
                pageName,
                hasCommentHashtag: !isNil(selectedTag),
                commentType: getCommentLevel(replyingTo),
                user_id: userId,
                nickname: userState?.nickname,
                email: userState?.email_address,
            });

            setComment("");
            setReplyingTo(null);
            setReplyingToCommentType(null);
            setReplyingToTagId(null);
            queryClient.invalidateQueries(["userComments", userId]);

            // Used generic invalidation here because we are dealing with
            // two types of comment queries (Normal comments and Tag comments)
            queryClient.invalidateQueries(["comments"]);

            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingPage-property"],
            });
            queryClient.invalidateQueries({
                queryKey: ["listing-page-user-guess"],
            });
            if (usercommentsData?.total === 0) {
                toast.success("👏 First comment posted – you’re on the map!");
            }
        },
        onError: (error) => {
            console.error("Error posting comment:", error);
            toast.error(error.response?.data?.message || "Failed to post comment");
        },
    });

    const { mutate: deleteCommentMutation } = useMutation({
        mutationFn: ({ userId, commentId }) => {
            if (selectedTag) {
                // If a specific tag is selected, use tag comment delete endpoint
                return deleteTagComment(userId, propertyId, selectedTag.id, commentId);
            } else {
                // If no tag is selected, check if the comment to delete is a tag comment
                const commentToDelete = allComments.find((comment) => comment.id === commentId);

                if (commentToDelete && commentToDelete.Tag) {
                    return deleteTagComment(userId, propertyId, commentToDelete.Tag.id, commentId);
                } else {
                    return deleteComment(userId, commentId);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["comments", propertyId, selectedTag?.id]);
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            toast.success("Comment deleted successfully");
        },
        onError: (error) => {
            console.error("Error deleting comment:", error);
            toast.error(error.response?.data?.message || "Failed to delete comment");
        },
    });

    const handleReply = (parentId, content) => {
        if (replyingTo === parentId) {
            // If clicking reply on the same comment, cancel the reply
            setReplyingTo(null);
            setReplyingToCommentType(null);
            setReplyingToTagId(null);
            setComment("");
        } else {
            // Start a new reply
            setReplyingTo(parentId);
            setComment(content || "");

            // Determine the comment type and store it
            const { isTagComment, tagId } = determineCommentType(parentId, commentsToDisplay);

            if (isTagComment && tagId) {
                setReplyingToCommentType("tag");
                setReplyingToTagId(tagId);
            } else {
                setReplyingToCommentType("normal");
                setReplyingToTagId(null);
            }

            if (isMobile) {
                if (isIOS) {
                    if (inputRef.current) {
                        inputRef.current.focus();
                        inputRef.current.click();
                    }
                } else {
                    // Focus input, then after keyboard opens, scroll comment into view
                    setTimeout(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                        setTimeout(() => {
                            const commentElement = document.getElementById(`comment-${parentId}`);
                            if (commentElement) {
                                commentElement.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        }, 400); // Wait for keyboard to open
                    }, 100);
                }
            } else {
                // On desktop, scroll immediately
                const commentElement = document.getElementById(`comment-${parentId}`);
                if (commentElement) {
                    setTimeout(() => {
                        commentElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 100);
                }
            }
        }
    };

    const handleSubmit = () => {
        if (!isAuthenticated) {
            if (!isEmpty(listing_address)) {
                navigate("/social?login=true");
                return;
            }
            customLoginWithRedirect({
                authorizationParams: {
                    redirect_uri: isEmpty(listing_address)
                        ? window.location.origin + "/social"
                        : window.location.origin + "/social/" + property.full_address.replace(/\s+/g, "-"),
                },
                appState: {
                    toggleProfile: true,
                    version: "social",
                    showSetNicknameModal: true,
                    event: {
                        name: "tutorial_register_click",
                        referral_code: localStorage.getItem("referral_code"),
                    },
                },
            });
            return;
        }

        if (!comment.trim()) {
            toast.error("Please enter a comment");
            return;
        }
        submitComment({ content: comment, parentId: replyingTo });
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    function filterComments(comments) {
        return comments
            .map((comment) => {
                if (comment.replies && comment.replies.length > 0) {
                    // Recursively filter children
                    const filteredReplies = filterComments(comment.replies);
                    // Keep this comment if:
                    // - It's not deleted, or
                    // - It has any non-deleted descendant
                    if (comment.content !== DELETED_COMMENT_TEXT || filteredReplies.length > 0) {
                        return { ...comment, replies: filteredReplies };
                    }
                    // Otherwise, filter it out
                    return null;
                } else {
                    // No children: only keep if not deleted
                    return comment.content !== DELETED_COMMENT_TEXT ? comment : null;
                }
            })
            .filter(Boolean);
    }

    const filteredComments = filterComments(commentsToDisplay);

    useEffect(() => {
        if (show && socialState?.commentId && commentsContainerRef.current && commentsToDisplay.length > 0) {
            const commentElement = document.getElementById(`comment-${socialState.commentId}`);
            if (commentElement) {
                setTimeout(() => {
                    commentElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    commentElement.classList.add("tw-bg-blue-50");
                    setTimeout(() => {
                        commentElement.classList.remove("tw-bg-blue-50");
                    }, 2000);
                }, 500);
            }
        }
    }, [show, socialState?.commentId, commentsToDisplay]);

    // For mobile - adjust drawer height
    useEffect(() => {
        if (!isMobile) return;
        const updateDrawerHeight = () => {
            if (window.visualViewport) {
                const vh = window.visualViewport.height;
                // Use px value if keyboard is open (viewport height is less than window height)
                if (vh < window.innerHeight) {
                    setDrawerHeight(`${vh}px`);
                } else {
                    setDrawerHeight(DEFAULT_DRAWER_HEIGHT);
                }
            }
        };
        window.visualViewport?.addEventListener("resize", updateDrawerHeight);
        // Initial adjustment
        updateDrawerHeight();
        return () => {
            window.visualViewport?.removeEventListener("resize", updateDrawerHeight);
        };
    }, [show]);

    const handleHideModal = () => {
        setSelectedTag(tag);
        setSocialState({
            ...socialState,
            property: null,
            guess: null,
            commentId: null,
        });
        handleClose();
    };

    const handleDeleteComment = (commentId) => {
        if (userId) {
            deleteCommentMutation({ userId, commentId });
        }
    };

    const handleBeNiceClose = () => {
        localStorage.setItem("seenBeNiceModal", "true");
        setShowBeNice(false);
        // Optionally, focus the input again
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    function getReactionPlaceholder(userReactions) {
        // Check if userReactions is empty
        if (userReactions?.data?.length === 0) {
            // Return a default placeholder or handle this case appropriately
            return "Type your comment here..";
        }

        const firstReaction = userReactions?.data[0]?.reaction_type;
        return unicodeReactionPromptMap[firstReaction] || "Type your comment here..";
    }

    useEffect(() => {
        if (show && isAuthenticated && usercommentsData?.total === 0) {
            const seen = localStorage.getItem("seenBeNiceModal");
            if (!seen) {
                setShowBeNice(true);
            }
        }
    }, [show, isAuthenticated, usercommentsData?.total]);

    const BeNiceModal = ({ show, onClose }) =>
        show ? (
            <div className="tw-fixed tw-inset-0 tw-bg-black/30 tw-flex tw-items-center tw-justify-center tw-z-50">
                <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-p-8 tw-max-w-md tw-w-full tw-text-center">
                    <div className="tw-text-2xl tw-font-bold tw-mb-2">👋 Just a quick note</div>
                    <div className="tw-text-gray-600 tw-mb-4">
                        We’re building a community where everyone feels welcome to share, react, and discuss.
                        <br />
                        <br />
                        Before you post your first comment, a simple ask:
                        <br />
                        <span className="tw-font-semibold">Be constructive. Be considerate.</span>
                        <br />
                        <br />
                        Let’s keep it fun.
                    </div>
                    <button
                        className="tw-bg-[#F69929] tw-text-white tw-font-semibold tw-py-2 tw-px-8 tw-rounded-lg tw-text-lg"
                        onClick={onClose}
                    >
                        Got it 👍
                    </button>
                </div>
            </div>
        ) : null;

    return (
        <ResponsiveWrapper
            show={show}
            onHide={handleHideModal}
            centered
            className="comments-modal"
            drawerHeight={drawerHeight}
        >
            <BeNiceModal show={showBeNice} onClose={handleBeNiceClose} />
            <div className="tw-relative tw-bg-white tw-flex tw-flex-col tw-h-full sm:tw-h-[600px] tw-overflow-hidden">
                {/* Header */}
                <div className="tw-relative tw-border-b tw-py-3 tw-px-4">
                    {selectedTag && tagObj ? (
                        !hideTag && (
                            <div className="tw-mt-6">
                                <TagVoteBar
                                    tag={tagObj}
                                    taggableId={taggableId}
                                    tagId={tagObj.id}
                                    userId={userId}
                                    hideVoteButtons={hideVoteButtons}
                                    propertyId={propertyId}
                                />
                            </div>
                        )
                    ) : (
                        <div className="tw-flex tw-text-[18px] tw-items-center tw-justify-center tw-py-1 tw-font-semibold tw-gap-2">
                            <MdInsertComment className="tw-h-[15px] tw-w-[15px]" />
                            Comments
                        </div>
                    )}
                    <button
                        className="tw-bg-white tw-absolute tw-top-0 tw-right-2 tw-p-2 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors"
                        onClick={handleHideModal}
                    >
                        <X className="tw-text-black tw-h-[24px] tw-w-[24px]" />
                    </button>
                </div>

                {/* Comment List */}
                <div
                    ref={commentsContainerRef}
                    className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-overscroll-contain"
                >
                    {isLoading ? (
                        [...Array(3)].map((_, index) => <CommentSkeleton key={index} />)
                    ) : filteredComments.length > 0 ? (
                        <>
                            {filteredComments.map((comment) => (
                                <Comment
                                    key={comment.id}
                                    id={`comment-${comment.id}`}
                                    comment={comment}
                                    onReply={handleReply}
                                    showDeleteButton={true}
                                    onDelete={handleDeleteComment}
                                    isReplyingTo={replyingTo === comment.id}
                                    currentUserId={userId}
                                    onSubmitReply={submitComment}
                                    replyingTo={replyingTo}
                                    hasParent={false}
                                    hasChildren={comment.replies && comment.replies.length > 0}
                                    tag={selectedTag}
                                />
                            ))}
                            {isFetchingNextPage && <CommentSkeleton />}
                            {hasNextPage && !isFetchingNextPage && (
                                <div className="tw-flex tw-justify-center tw-py-1">
                                    <button
                                        onClick={() => fetchNextPage()}
                                        className="tw-bg-white tw-py-2 tw-rounded-full tw-text-[13px] tw-text-[#6C26C4] tw-font-medium hover:tw-text-[#5a1fa3] hover:tw-bg-gray-50 tw-transition-colors tw-border tw-border-[#D9D9D9]"
                                    >
                                        Load More Comments
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="tw-text-center tw-text-gray-500 tw-py-8">No comments yet</div>
                    )}
                </div>

                {/* Comment Input */}
                <div className="tw-border-t tw-p-3 tw-bg-white">
                    {tags && tags.length > 0 && !hideTag && (
                        <div className="tw-mb-2">
                            <TagList
                                tags={tags}
                                selectedTag={selectedTag}
                                onTagClick={setSelectedTag}
                                propertyId={propertyId}
                            />
                        </div>
                    )}
                    {!socialState?.guess && (
                        <div className="tw-flex tw-items-start tw-gap-2">
                            <AutoResizeTextarea
                                ref={inputRef}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={isSubmitting}
                                placeholder={
                                    selectedTag
                                        ? `Leave a comment about #${selectedTag.name}`
                                        : replyingTo
                                          ? "Write a reply..."
                                          : getReactionPlaceholder(userReactions)
                                }
                            />
                            <button
                                className={clsx(
                                    "tw-bg-[#F69929] tw-p-2 tw-rounded-full tw-border-[1px] tw-border-solid tw-border-black hover:tw-bg-[#e89355] tw-transition-colors tw-self-end",
                                    { "tw-opacity-50 tw-cursor-not-allowed": isSubmitting },
                                    { "tw-mb-2": drawerHeight !== DEFAULT_DRAWER_HEIGHT }
                                )}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                <IoIosSend className="tw-h-[20px] tw-w-[20px] tw-text-black" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </ResponsiveWrapper>
    );
};

CommentsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    propertyId: PropTypes.string.isRequired,
    tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }),
    tags: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            creator: PropTypes.shape({
                id: PropTypes.string.isRequired,
            }),
            taggableId: PropTypes.string.isRequired,
        })
    ),
};

export default CommentsModal;
